import { decryptId } from 'common/decrypt';
import { axiosServicesV1, axiosServicesV3 } from 'utils/services';

export const getOnGoingCases = async (id: string) => {
    try {
        const decrypted = await decryptId(id || '', 'decrypt');
        const response = await axiosServicesV1(`/data_iris/D_GetCaseListByID?IDNo=${decrypted}`);
        return response.data.pxResults;
    } catch (err: any) {
        throw new Error(err);
    }
};

export const getOnGoingEvent = async (id: string) => {
    try {
        const response = await axiosServicesV1(`/case_iris/${id}`, {
            headers: { 
                caseid: id || '',
                pzinskey: id || ''
            }
        });
        if (response?.headers?.etag) {
            return response.headers.etag;
        }
    } catch (err) {
        console.error(err);
    }

    return '';
};

export const getLicences = async (id: string, LICNoOrLICName: string = '') => {
    const decrypted = await decryptId(id || '', 'decrypt');

    const response = await axiosServicesV1(`/data_iris/D_GetFELicencesByApplicantID?IDNo=${decrypted}&LICNoOrLICName=${LICNoOrLICName}`);
    return response.data.pxResults;
};

export const getLicenceList = async (id: string) => {
    const decrypted = await decryptId(id || '', 'decrypt');

    const response = await axiosServicesV1(`/data_iris/D_GetFEApplicationLICList?IDNo=${decrypted}`);
    return response.data.pxResults;
};

export const WithdrawEvent = async (id: string, message: string, etag: string, stage?: string) => {
    try {
        const response = await axiosServicesV1.put(
            `/case_iris/${id}?actionID=pyChangeStage`,
            {
                content: {
                    pyGotoStage: stage ?? 'ALT1',
                    WithdrawalRemarks: message,
                    WithdrawalDate: new Date()
                }
            },
            {
                headers: {
                    'If-Match': etag,
                    caseid: id,
                    pzinskey: id
                }
            }
        );
        if (response?.status) {
            return response.status;
        }
    } catch (err) {
        console.error(err);
    }

    return '';
};

// eslint-disable-next-line consistent-return
export const downloadCertificate = async (file: Record<string, any>, caseTypeID?: string) => {
    try {
        let { links: { download: { href = '' } = {} } = {} } = file;
        if (href === null || href === '') {
            href = file?.DownloadLink;
        }
        const certificateLink = await axiosServicesV1.get(`${href}`, {
            headers: { 
                caseid: caseTypeID || '',
                pzinskey: caseTypeID || ''
            }
        });
        return certificateLink;
    } catch (error) {
        console.error(error);
    }
};

export const validateUEN = async (uen?: string) => {
    if (uen) {
        try {
            const decryptedUEN = await decryptId(uen || '', 'decrypt');
            const response = await axiosServicesV3('reg-importer-validate/', { headers: { uen: decryptedUEN } });
            return Boolean(response.data?.message === 'Valid');
        } catch (err) {
            console.log(err);
        }
    }

    return false;
};

export const ReadMessage = async (caseid: string, date: string, etag: string, readMessageEndpoint: string, pzinsKeyValue: string) => {
    try {
        const response = await axiosServicesV1.patch(
            readMessageEndpoint,
            { AppComms: { ApplicationRefNo: caseid, DateRead: date, ReadReceipt: 'Read' } },
            {
                headers: {
                    'If-Match': etag,
                    pzinskey: pzinsKeyValue
                }
            }
        );
        if (response?.status) {
            return response.status;
        }
    } catch (err) {
        console.error(err);
    }
    return false;
};

export const getPendingPayments = async (id: string) => {
    try {
        const decrypted = await decryptId(id || '', 'decrypt');
        const response = await axiosServicesV1(`/data_iris/D_GetInvoiceListForBulkPayment?IDNo=${decrypted}`);
        return response.data.pxResults;
    } catch (err: any) {
        throw new Error(err);
    }
};

export const getIsCAProfile = async (id: string) => {
    try {
        const decrypted = await decryptId(id || '', 'decrypt');
        const response = await axiosServicesV1(`/data_iris/D_GetIsCAProfile?IDNo=${decrypted}`);
        return response.data.pxResults;
    } catch (err: any) {
        throw new Error(err);
    }
};

export const getRCADetailsForProfile = async (id: string) => {
    try {
        const decrypted = await decryptId(id || '', 'decrypt');
        const response = await axiosServicesV1(`/data_iris/D_GetRCADetailsForProfile?IDNo=${decrypted}`);
        return response.data.pxResults;
    } catch (err: any) {
        throw new Error(err);
    }
};

export const getRCAProfilePenaltyFlags = async (id: string) => {
    try {
        const decrypted = await decryptId(id || '', 'decrypt');
        const response = await axiosServicesV1(`/data/D_GetCAProfilePenaltyFlags?IDNo=${decrypted}`);
        return response.data;
    } catch (err: any) {
        throw new Error(err);
    }
};

/**
 * Validate if a licence can have a new application created (Amend/Renew/Cancel)
 * Checks if there is already an ongoing application for this licence
 * @param caseID - The case ID (e.g., "IMDA-IRIS-WORK-LICENCE L-1810253")
 * @returns Object with validation result
 */
export const validateLicenceForNewApplication = async (caseID: string) => {
    try {
        const response = await axiosServicesV1(`/data/D_ValidateLicenceDetailsFEP?CaseID=${caseID}`, {
            headers: { 
                caseid: caseID,
                pzinskey: caseID
            }
        });
        const data = response.data;

        if (data.HasErrorMessages === 'true' || data.HasErrorMessages === true) {
            const errorMessages = data.ErrorPageList || [];
            const hasOngoingApp = errorMessages.some((error: any) => error.ErrorMessage === 'HaveOngoingApplication');

            if (hasOngoingApp) {
                return {
                    isValid: false,
                    hasOngoingApplication: true,
                    message:
                        'You have an ongoing application for this licence. Please wait for the current application to be processed, or withdraw it before creating a new one.'
                };
            }

            // Other errors
            return {
                isValid: false,
                hasOngoingApplication: false,
                message: 'Unable to proceed with this action. Please try again later.'
            };
        }

        return {
            isValid: true,
            hasOngoingApplication: false
        };
    } catch (err: any) {
        console.error('Error validating licence:', err);
        throw new Error('Failed to validate licence. Please try again.');
    }
};

export const getAuthorisedSubmissionsForCompany = async (id: string) => {
    try {
        const decrypted = await decryptId(id || '', 'decrypt');
        const response = await axiosServicesV1(`/data/D_GetAuthorisedSubmissionsForCompany?IDNo=${decrypted}`);
        return response.data.pxResults;
    } catch (err: any) {
        throw new Error(err);
    }
};

export const checkIfProfileAuthorisedCA = async (idNo: string, uen: string) => {
    try {
        const decryptedIdNo = await decryptId(idNo || '', 'decrypt');
        const decryptedUEN = await decryptId(uen || '', 'decrypt');
        const response = await axiosServicesV1(`/data/D_CheckIfProfileAuthorisedCA?IDNo=${decryptedIdNo}&UEN=${decryptedUEN}`);
        return response.data;
    } catch (err: any) {
        throw new Error(err);
    }
};
